/**
 * Premium Features Configuration
 *
 * This file manages the premium features system for Wallstreetle.
 * It supports three modes:
 * 1. FREE - All features are free, no premium restrictions
 * 2. HIDE - Premium features exist but UI elements are hidden
 * 3. STANDARD - Normal premium behavior with full UI
 *
 * Configuration is loaded from environment variables with sensible defaults.
 */

import {
  CategorySlug,
  PREMIUM_CATEGORIES as DEFAULT_PREMIUM_CATEGORIES,
  isValidCategory as isValidCategorySlug
} from './categoryConfig';

export type PremiumMode = 'FREE' | 'HIDE' | 'STANDARD';

export type StockCategory = CategorySlug;

export interface PremiumUIControls {
  /** Show/hide the "Go Premium" button in header navigation */
  showButton: boolean;

  /** Show/hide crown/lock icons on premium category buttons */
  showLocks: boolean;

  /** Show/hide upgrade prompt modals when accessing premium features */
  showModals: boolean;

  /** Show/hide premium sentiment voting history feature */
  showSentimentHistory: boolean;
}

export interface PremiumFeatureToggles {
  /** Whether premium categories are gated */
  gatePremiumCategories: boolean;

  /** Whether sentiment history is premium-only */
  gateSentimentHistory: boolean;

  /** Whether private leaderboards have limits */
  gatePrivateLeaderboards: boolean;
}

export interface PremiumConfiguration {
  /** Current premium mode */
  mode: PremiumMode;

  /** List of categories that require premium access */
  premiumCategories: StockCategory[];

  /** UI visibility controls */
  uiControls: PremiumUIControls;

  /** Feature-specific toggles */
  features: PremiumFeatureToggles;

  /** Whether configuration was loaded from environment or using defaults */
  isDefault: boolean;
}

/**
 * Parse comma-separated category list from environment variable
 */
function parseCategoryList(envVar: string | undefined, defaultCategories: StockCategory[]): StockCategory[] {
  if (!envVar) {
    return defaultCategories;
  }

  const categories = envVar
    .split(',')
    .map(cat => cat.trim() as StockCategory)
    .filter(cat => isValidCategory(cat));

  return categories.length > 0 ? categories : defaultCategories;
}

/**
 * Check if a category name is valid
 */
function isValidCategory(category: string): category is StockCategory {
  return isValidCategorySlug(category);
}

/**
 * Parse boolean from environment variable
 */
function parseBoolean(envVar: string | undefined, defaultValue: boolean): boolean {
  if (!envVar) {
    return defaultValue;
  }
  return envVar.toLowerCase() === 'true';
}

/**
 * Parse premium mode from environment variable
 */
function parsePremiumMode(envVar: string | undefined): PremiumMode {
  const mode = envVar?.toUpperCase();
  if (mode === 'FREE' || mode === 'HIDE' || mode === 'STANDARD') {
    return mode;
  }
  return 'STANDARD'; // Default mode
}

/**
 * Get UI controls based on mode and environment variables
 */
function getUIControls(mode: PremiumMode): PremiumUIControls {
  // Use environment variables or mode-based defaults
  // STANDARD: show premium UI, HIDE/FREE: hide premium UI
  const baseDefault = mode === 'STANDARD' ? true : false;

  return {
    showButton: parseBoolean(
      process.env.REACT_APP_PREMIUM_UI_SHOW_BUTTON,
      baseDefault
    ),
    showLocks: parseBoolean(
      process.env.REACT_APP_PREMIUM_UI_SHOW_LOCKS,
      baseDefault
    ),
    showModals: parseBoolean(
      process.env.REACT_APP_PREMIUM_UI_SHOW_MODALS,
      baseDefault
    ),
    showSentimentHistory: parseBoolean(
      process.env.REACT_APP_PREMIUM_UI_SHOW_SENTIMENT_HISTORY,
      baseDefault
    ),
  };
}

/**
 * Get feature toggles based on mode
 */
function getFeatureToggles(mode: PremiumMode): PremiumFeatureToggles {
  // In FREE mode, don't gate any features
  if (mode === 'FREE') {
    return {
      gatePremiumCategories: false,
      gateSentimentHistory: false,
      gatePrivateLeaderboards: false,
    };
  }

  // In HIDE and STANDARD modes, gate features normally
  return {
    gatePremiumCategories: true,
    gateSentimentHistory: true,
    gatePrivateLeaderboards: true,
  };
}

/**
 * Load premium configuration from environment variables
 */
function loadPremiumConfiguration(): PremiumConfiguration {
  const mode = parsePremiumMode(process.env.REACT_APP_PREMIUM_MODE);

  // Default premium categories (imported from central config)
  const defaultPremiumCategories = [...DEFAULT_PREMIUM_CATEGORIES] as StockCategory[];

  const premiumCategories = parseCategoryList(
    process.env.REACT_APP_PREMIUM_CATEGORIES,
    defaultPremiumCategories
  );

  const uiControls = getUIControls(mode);
  const features = getFeatureToggles(mode);

  // Check if any environment variables were actually set
  const isDefault = !process.env.REACT_APP_PREMIUM_MODE;

  return {
    mode,
    premiumCategories,
    uiControls,
    features,
    isDefault,
  };
}

/**
 * Runtime configuration that can be updated via admin UI
 * Starts with environment-based config but can be overridden
 */
class PremiumConfigManager {
  private config: PremiumConfiguration;
  private listeners: Array<(config: PremiumConfiguration) => void> = [];

  constructor() {
    // Load initial configuration from environment
    this.config = loadPremiumConfiguration();

    // Try to load runtime overrides from localStorage
    this.loadRuntimeConfig();
  }

  /**
   * Get current configuration
   */
  getConfig(): PremiumConfiguration {
    return { ...this.config };
  }

  /**
   * Update configuration at runtime (admin UI)
   */
  updateConfig(updates: Partial<PremiumConfiguration>): void {
    this.config = {
      ...this.config,
      ...updates,
      isDefault: false,
    };

    // Persist to localStorage
    this.saveRuntimeConfig();

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Reset to environment-based configuration
   */
  resetToDefaults(): void {
    this.config = loadPremiumConfiguration();
    localStorage.removeItem('wallstreetle_premium_config');
    this.notifyListeners();
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: (config: PremiumConfiguration) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Load runtime configuration from localStorage
   */
  private loadRuntimeConfig(): void {
    try {
      const stored = localStorage.getItem('wallstreetle_premium_config');
      if (stored) {
        const runtimeConfig = JSON.parse(stored) as Partial<PremiumConfiguration>;
        this.config = {
          ...this.config,
          ...runtimeConfig,
        };
      }
    } catch (error) {
      console.warn('Failed to load runtime premium config:', error);
    }
  }

  /**
   * Save runtime configuration to localStorage
   */
  private saveRuntimeConfig(): void {
    try {
      localStorage.setItem('wallstreetle_premium_config', JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save runtime premium config:', error);
    }
  }

  /**
   * Notify all listeners of configuration changes
   */
  private notifyListeners(): void {
    const config = this.getConfig();
    this.listeners.forEach(listener => {
      try {
        listener(config);
      } catch (error) {
        console.error('Error in premium config listener:', error);
      }
    });
  }
}

/**
 * Singleton instance of premium configuration manager
 */
export const premiumConfigManager = new PremiumConfigManager();

/**
 * Get current premium configuration
 */
export function getPremiumConfig(): PremiumConfiguration {
  return premiumConfigManager.getConfig();
}

/**
 * Check if a category requires premium access
 */
export function isCategoryPremium(category: StockCategory): boolean {
  const config = getPremiumConfig();

  // In FREE mode, no categories are premium
  if (config.mode === 'FREE') {
    return false;
  }

  return config.premiumCategories.includes(category);
}

/**
 * Check if premium UI elements should be shown
 */
export function shouldShowPremiumUI(element: keyof PremiumUIControls): boolean {
  const config = getPremiumConfig();
  return config.uiControls[element];
}

/**
 * Check if a feature should be gated by premium
 */
export function isFeatureGated(feature: keyof PremiumFeatureToggles): boolean {
  const config = getPremiumConfig();
  return config.features[feature];
}

/**
 * Export the current configuration for debugging
 */
export function debugPremiumConfig(): void {
  const config = getPremiumConfig();
  console.group('Premium Configuration');
  console.log('Mode:', config.mode);
  console.log('Premium Categories:', config.premiumCategories);
  console.log('UI Controls:', config.uiControls);
  console.log('Feature Toggles:', config.features);
  console.log('Is Default:', config.isDefault);
  console.groupEnd();
}

// Make debug function available in development
if (process.env.NODE_ENV === 'development') {
  (window as any).__debugPremiumConfig = debugPremiumConfig;
}
